import { browserExtensionClient } from 'clients/BrowserExtensionClient/BrowserExtensionClient';
import { getActiveTokenStatically } from 'shared/legacy/contexts/AuthContext';
import { queryClient } from 'config/queryClient';
import { hosts } from 'config';
import { QueryKey } from 'types/query';
import { getIsFeatureToggleEnabled } from '@/features/feature-toggle/lib';
import type { IndeedArgs, IndeedConfig } from './indeed.types';
import {
  findConversationsByContextDiscoveryPayload,
  getConversationsPayload,
  getConversationEventsPayloadTemplate,
  contactSummaryPayloadTemplate,
} from './indeed.payloads';

const parseTenantIdFromToken = (token: string | null): string => {
  try {
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return String(payload?.tenant_id ?? payload?.tenantId ?? '');
  } catch {
    return '';
  }
};

const parseEmailFromToken = (token: string | null): string => {
  try {
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.user?.email ?? payload?.name ?? '';
  } catch {
    return '';
  }
};

export const buildIndeedArgs = (indeedConfig: IndeedConfig, userEmail: string): IndeedArgs | null => {
  if (!indeedConfig.isLoggedIn) return null;

  return {
    url: 'https://apis.indeed.com/graphql',
    headers: indeedConfig.headers,
    platform: 'indeed',
    findConversationsByContextBody: JSON.stringify(findConversationsByContextDiscoveryPayload),
    getConversationsBody: JSON.stringify(getConversationsPayload),
    getConversationEventsBody: JSON.stringify(getConversationEventsPayloadTemplate),
    contactSummaryBody: JSON.stringify(contactSummaryPayloadTemplate),
    userEmail,
    notificationsApiUrl: hosts.notificationsApi() ?? '',
    searchApiUrl: hosts.searchApi() ?? '',
  };
};

export const sendIndeedConfig = async (token?: string, indeedConfig?: IndeedConfig): Promise<void> => {
  try {
    const resolvedToken = token ?? (await getActiveTokenStatically())[0];

    const tenantId = parseTenantIdFromToken(resolvedToken);
    const userEmail = parseEmailFromToken(resolvedToken);

    const resolvedConfig =
      indeedConfig ?? queryClient.getQueriesData<IndeedConfig>([QueryKey.useIndeedCommonModelQuery]).at(0)?.[1];

    const indeedArgs = resolvedConfig ? buildIndeedArgs(resolvedConfig, userEmail) : null;

    await browserExtensionClient.sendMessage('updateConfig', {
      token: resolvedToken,
      tenantId,
      messageFetcherEnabled: getIsFeatureToggleEnabled((t) => t.INDEED_POLLING_ENABLED),
      requestHeaderCaptureUrl: 'https://apis.indeed.com/*',
      ...(indeedArgs ? { messageFetcherArgs: indeedArgs } : {}),
    });
  } catch (err) {
    try {
      newrelic.addPageAction('IndeedConfigSyncError', {
        error: err instanceof Error ? err.message : String(err),
      });
    } catch {
      // newrelic not available
    }
  }
};
